import React from "react"
import {useEffect} from "react"
import {useComputed} from "@preact/signals-react"
import {useSearchParams} from "react-router-dom"
import {Grid} from "@mui/material"
import { LoadingIconSeeker } from "@ui-seeker/core"
import { AppFilterPanel, AppPageTabs, AppResultsSkeleton } from "@components"
import {USER_SETTINGS_TABS_ID} from "./@constants"
import {useUserSettingsContent, useUserSettingsTabs} from "./@hooks"
import {userSettingsTab} from "./@signals"
import {resolveUrlTab, tabFromSearch} from "./@services/tabs"
import UserSettingsAccountTab from "./AccountTab"
import UserSettingTierTab from "./TierTab"
import UserSettingOfficesGroupTab from "./OfficesGroupTab"
import UserSettingsWebsitesTab from "./WebsitesTab"
import UserSettingsItemsTab from "./ItemsTab"
import UserSettingsSitesGroupTab from "./SitesTab"
import UserSettingWebSupplierGroupTab from "./WebSupplierGroupTab"
import UserSettingAcrissGroupTab from "./AcrissGroupTab"


export default function UserSettingContent() {
	const { tabs, getAllData } = useUserSettingsContent()
	const { requestTab, restoreUrl } = useUserSettingsTabs()
	const [searchParams] = useSearchParams()
	const selectedTab = userSettingsTab
	const firstLoading = useComputed(() => selectedTab.value === null)
	const allowed = tabs.map(t => t.value)
	const allowedKey = allowed.join('|')

	useEffect(() => {
		getAllData()
		selectedTab.value = tabFromSearch(searchParams.get('tab'), allowed, USER_SETTINGS_TABS_ID.ACCOUNT)
	}, [])

	// The URL changed with the page mounted (or a tab it asked for became available): ask for that tab
	// through the guard; staying puts the open tab back in the URL (TL, plan-review block 3)
	useEffect(() => {
		if (selectedTab.value === null) return
		const want = resolveUrlTab({
			current: selectedTab.value, param: searchParams.get('tab'), allowed, fallback: USER_SETTINGS_TABS_ID.ACCOUNT,
		})
		if (want) requestTab(want, { onStay: restoreUrl })
	}, [searchParams, allowedKey])


	return <>
		{ !firstLoading.value && <AppFilterPanel
			toggles={<AppPageTabs
				disabled={firstLoading.value}
				tabs={tabs.map(({ isLoading, ...tab }) => ({
					...tab, icon: isLoading ? <LoadingIconSeeker /> : (tab.icon ?? null),
				}))}
				value={selectedTab.value}
				onChange={(_, newValue) => requestTab(newValue)}
				stackProps={{ sx: { mb: 0 } }}
			/>}
		/>}

		<Grid container spacing={2}>
			{firstLoading.value  && <Grid item xs={12}>
				<AppResultsSkeleton rows={10} columns={5}/>
			</Grid>}

			{ selectedTab.value === USER_SETTINGS_TABS_ID.ACCOUNT && <Grid item xs={12}>
				<UserSettingsAccountTab />
			</Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.TIER && <Grid item xs={12}>
				<UserSettingTierTab />
			</Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.OFFICE_GROUP && <Grid item xs={12}>
				<UserSettingOfficesGroupTab />
			</Grid> }

	        { selectedTab.value === USER_SETTINGS_TABS_ID.SITE_GROUP && <Grid item xs={12}>
	            <UserSettingsSitesGroupTab />
	        </Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.WEB_SUPPLIER_GROUP && <Grid item xs={12}>
				<UserSettingWebSupplierGroupTab />
			</Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.ACRISS_GROUP && <Grid item xs={12}>
				<UserSettingAcrissGroupTab />
			</Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.COLORS_WEBSITE && <Grid item xs={12}>
				<UserSettingsWebsitesTab />
			</Grid> }

			{ selectedTab.value === USER_SETTINGS_TABS_ID.COLORS_ITEM && <Grid item xs={12}>
				<UserSettingsItemsTab />
			</Grid> }

		</Grid>
	</>
}
